import {t} from "@lingui/core/macro";
import classNames from "classnames";
import gql from "graphql-tag";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {CircularSpinner} from "@khanacademy/wonder-blocks-progress-spinner";
import {
    useTimeout,
    withActionScheduler,
    type WithActionSchedulerProps,
} from "@khanacademy/wonder-blocks-timing";
import {gqlOp, type GqlOperation} from "@repo/data-gql-core";
import {doNotTranslate} from "@repo/i18n";
import {RequestInfo} from "@repo/shared-core";
import {staticUrl} from "@repo/urls";

import FastlyQuery from "#data-access/gql/fastly-query.ts";

import type {
    getTeamPage_teamPage_entries_liveContent_contactLinks as GqlContactLinks,
    getTeamPage_teamPage_entries as GqlEntries,
    getTeamPage as GqlGetTeamPageResponse,
    getTeamPageVariables as GqlGetTeamPageVariables,
    getTeamPage_teamPage_entries_liveContent as GqlLiveContent,
} from "./__graphql-types__/getTeamPage.ts";

type TeamMemberCardProps = {
    info: GqlLiveContent;
    index: number;
    onCardHover?: (mouseOver: boolean) => void;
};

export const TeamMemberCard: React.FC<TeamMemberCardProps> = ({
    info,
    index,
    onCardHover,
}) => {
    const cardInfoRef = React.useRef<HTMLDivElement>(null);

    // Set the height of the portrait container to fit the card info
    const setHeight = React.useCallback(() => {
        const infoHeight = cardInfoRef.current?.clientHeight;
        if (infoHeight) {
            const portraitContainer = document.querySelector(
                ".portrait-container",
            ) as HTMLElement;
            portraitContainer.style.paddingBottom = `${infoHeight}px`;
        }
    }, []);

    const {clear, set: startTimeout} = useTimeout(setHeight, 50);

    const handleCardMouseEnter = () => {
        onCardHover && onCardHover(true);

        startTimeout();
    };

    const handleCardMouseLeave = () => {
        onCardHover && onCardHover(false);

        const portraitContainer = document.querySelector(
            ".portrait-container",
        ) as HTMLElement;
        portraitContainer.style.paddingBottom = "initial";

        clear();
    };

    const getLinkClass = (link: GqlContactLinks) => {
        const linkTypeClassMap = {
            BLOG: "contact-blog",
            TWITTER: "contact-twitter",
            GITHUB: "contact-github",
            KHAN_ACADEMY: "contact-ka",
            WEBSITE: "contact-home",
            // TODO(jvoll): Get a LinkedIn logo image to use here
            LINKEDIN: "contact-home",
        };

        return linkTypeClassMap[link.type] || "contact-home";
    };

    const getLinkLabel = (link: GqlContactLinks, name: string) => {
        const linkLabelMap = {
            BLOG: t`${name}'s blog`,
            TWITTER: t`${name}'s Twitter profile`,
            GITHUB: t`${name}'s Github profile`,
            KHAN_ACADEMY: t`${name}'s Khan Academy profile`,
            WEBSITE: t`${name}'s website`,
            LINKEDIN: t`${name}'s LinkedIn profile`,
        };

        return linkLabelMap[link.type] || t`${name}'s website`;
    };

    // We don't want to allow Blog, Twitter, or Website links for former KA
    // employees. This is because these sites may 404, or domains may be taken
    // sold and changed to something else, or Twitter accounts can be taken
    // over by others, etc. Former employees would have no way to keep these
    // current in our system, so it's best to just not show them.
    const isLinkAllowed = (isAlum: boolean) => (link: GqlContactLinks) => {
        const mightBeDangerous = {
            BLOG: true,
            TWITTER: true,
            WEBSITE: true,
        };
        return !(mightBeDangerous[link.type] && isAlum);
    };

    const renderContactLinks = (
        name: string,
        contactLinks: Array<GqlContactLinks>,
        keyRoot: string,
        isAlum: boolean,
    ) => (
        <p className="contact-links">
            {contactLinks.filter(isLinkAllowed(isAlum)).map((link, idx) => (
                <a
                    href={link.url}
                    target="_blank"
                    className={getLinkClass(link)}
                    aria-label={getLinkLabel(link, name)}
                    key={`${keyRoot}-link-${idx}`}
                />
            ))}
        </p>
    );

    const constructBio = (bio: string, keyRoot: string) => {
        const paragraphs = bio.split("\n");
        return paragraphs.map((paragraph, i) => (
            <p key={keyRoot + "-bio-" + i}>{paragraph}</p>
        ));
    };

    const {
        mainPhotoURL,
        hoverPhotoURL,
        name,
        altNames,
        title,
        bio,
        contactLinks,
        isAlum,
    } = info;

    const keyRootString = `card-${index}`;

    return (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- TODO: Address a11y error
        <div
            className="team-card"
            key={keyRootString}
            onMouseEnter={handleCardMouseEnter}
            onMouseLeave={handleCardMouseLeave}
        >
            <div className="card-photo">
                <img
                    alt=""
                    aria-hidden={true}
                    src={staticUrl(mainPhotoURL)}
                    className="normal"
                />
                <img
                    alt=""
                    aria-hidden={true}
                    // NOTE(diedra): We need an alternate img element here, even if
                    // there is no hoverPhoto for the individual, to stop the
                    // image from glitching out on hover.
                    src={staticUrl(hoverPhotoURL || mainPhotoURL)}
                    className="alternate"
                />
            </div>
            <div className="card-info" ref={cardInfoRef}>
                <h2 className="pulls">{name}</h2>

                {/** This section allows the search feature to surface people by alternate names. */}
                {altNames &&
                    altNames.map((altName) => (
                        <p
                            style={{display: "none"}}
                            key={`${index}-${name}-${altName}`}
                        >
                            {altName}
                        </p>
                    ))}

                <h3>{title}</h3>

                {bio && !isAlum && constructBio(bio, keyRootString)}

                {contactLinks &&
                    renderContactLinks(
                        name,
                        contactLinks,
                        keyRootString,
                        isAlum,
                    )}
            </div>
        </div>
    );
};

type MainSectionOwnProps = {
    entries: Array<GqlEntries>;
};
type MainSectionProps = MainSectionOwnProps & WithActionSchedulerProps;

const MainSectionImpl: React.FC<MainSectionProps> = ({entries}) => {
    const [isHovered, setIsHovered] = React.useState(false);

    return (
        <div
            className={classNames("portrait-container", {
                "over-team-card": isHovered,
            })}
        >
            {entries.map((entry, index) => (
                <TeamMemberCard
                    info={entry.liveContent}
                    index={index}
                    key={`entry-${index}`}
                    onCardHover={setIsHovered}
                />
            ))}
        </div>
    );
};

const MainSection = withActionScheduler(MainSectionImpl);

export const TEAM_PAGE_QUERY: GqlOperation<
    GqlGetTeamPageResponse,
    GqlGetTeamPageVariables
> = gqlOp(gql`
    query getTeamPage($name: TeamPageName!) {
        teamPage(name: $name) {
            entries {
                id
                liveContent {
                    id
                    name
                    isAlum
                    mainPhotoURL
                    hoverPhotoURL
                    title
                    bio
                    altNames
                    contactLinks {
                        type
                        url
                    }
                }
            }
        }
    }
`);

type SearchBarProps = {
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

const SearchBar: React.FC<SearchBarProps> = ({onChange}) => (
    <div className="employee-search">
        <input
            type="text"
            autoFocus={true}
            placeholder={t`Search by name or position...`}
            id="employee-search-box"
            onChange={onChange}
        />
    </div>
);

type Props = {
    footer: React.ReactNode;
    teamPageName: GqlGetTeamPageVariables["name"];
};

const TeamPageBody: React.FC<Props> = ({footer, teamPageName}) => {
    const [searchTerm, setSearchTerm] = React.useState("");

    return (
        <div id="the-team">
            <SearchBar onChange={(evt) => setSearchTerm(evt.target.value)} />
            <div className="employee-search-spacer" />
            <FastlyQuery
                query={TEAM_PAGE_QUERY}
                variables={{name: teamPageName}}
            >
                {(result) => {
                    if (
                        result.status === "loading" ||
                        result.status === "no-data"
                    ) {
                        return (
                            <View
                                style={{
                                    flex: 1,
                                    justifyContent: "center",
                                    alignItems: "center",
                                }}
                            >
                                <CircularSpinner size="large" />
                            </View>
                        );
                    }

                    if (
                        result.status === "success" &&
                        result.data.teamPage?.entries != null
                    ) {
                        return (
                            <MainSection
                                entries={result.data.teamPage.entries.filter(
                                    (entry) =>
                                        entry.liveContent.name
                                            .toLowerCase()
                                            .match(searchTerm.toLowerCase()),
                                )}
                            />
                        );
                    }

                    return null;
                }}
            </FastlyQuery>
            {footer}
            {RequestInfo.kaLocale === "en" && (
                <div style={{textAlign: "center"}}>
                    <p>
                        {doNotTranslate`For employment verification inquiries, please email employmentverification@khanacademy.org`}
                    </p>
                </div>
            )}
            <p>&nbsp;</p>
        </div>
    );
};

export default TeamPageBody;
